import React, { useEffect, useMemo, useState } from 'react';
import './App.css';

const API_BASE = process.env.REACT_APP_API_BASE || '';

const DEFAULT_STATIONS = [
  { crs: 'PLY', name: 'Plymouth' },
  { crs: 'DPT', name: 'Devonport' },
  { crs: 'DOC', name: 'Dockyard' },
  { crs: 'KEY', name: 'Keyham' },
  { crs: 'SBF', name: 'St Budeaux Ferry Road' },
  { crs: 'SBV', name: 'St Budeaux Victoria Road' },
  { crs: 'BFE', name: 'Bere Ferrers' },
  { crs: 'BAS', name: 'Bere Alston' },
  { crs: 'GSL', name: 'Gunnislake' }
];

function initialRoute() {
  const path = window.location.pathname.toLowerCase();
  const params = new URLSearchParams(window.location.search);
  return {
    page: path.includes('arrivals') ? 'arrivals' : 'departures',
    full: path.includes('fullboard') || params.get('full') === '1',
    station: (params.get('station') || params.get('crs') || 'PLY').toUpperCase().slice(0, 3)
  };
}

function updateUrl({ page, station, full }) {
  const path = full ? '/fullboard' : page === 'arrivals' ? '/arrivals' : '/departures';
  const params = new URLSearchParams();
  params.set('station', station || 'PLY');
  if (full && page === 'arrivals') params.set('type', 'arrivals');
  if (full && page !== 'arrivals') params.set('type', 'departures');
  window.history.pushState({}, '', `${path}?${params.toString()}`);
}

function formatClock(date = new Date()) {
  return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function boardLabel(page) {
  return page === 'arrivals' ? 'Arrivals' : 'Departures';
}

function statusClass(row) {
  const text = `${row.expected || ''} ${row.status || ''} ${row.cancelReason || ''} ${row.delayReason || ''}`.toLowerCase();
  if (text.includes('cancel')) return 'danger';
  if (text.includes('delay') || text.includes('late')) return 'warn';
  if (text.includes('on time')) return 'good';
  return 'info';
}

function displayExpected(row) {
  const value = row.expected || '—';
  if (/on time/i.test(value)) return 'On time';
  return value;
}

function BoardTable({ rows, page, compact = false }) {
  if (!rows?.length) {
    return <div className="empty-board">No train services returned for this station yet.</div>;
  }
  return (
    <div className={compact ? 'rail-table rail-table-compact' : 'rail-table'}>
      <div className="rail-head">
        <span>Time</span>
        <span>{page === 'arrivals' ? 'From' : 'Destination'}</span>
        <span>Plat</span>
        <span>Expected</span>
        <span>Operator</span>
      </div>
      {rows.map((row, index) => {
        const mainPlace = page === 'arrivals' ? row.origin : row.destination;
        return (
          <div className="rail-row" key={`${row.serviceId || 'svc'}-${index}`}>
            <span className="time-cell">{row.scheduled || '—'}</span>
            <span className="place-cell">
              <strong>{mainPlace || 'Unknown'}</strong>
              {!compact && row.callingPoints?.length > 0 && (
                <small>{page === 'arrivals' ? 'Called at ' : 'Calling at '}{row.callingPoints.slice(0, 5).join(', ')}{row.callingPoints.length > 5 ? '…' : ''}</small>
              )}
            </span>
            <span className="platform-cell">{row.platform || '—'}</span>
            <span className={`expected-cell ${statusClass(row)}`}>{displayExpected(row)}</span>
            <span className="operator-cell">{row.operator || 'National Rail'}</span>
          </div>
        );
      })}
    </div>
  );
}

function FullscreenBoard({ board, page, station, onClose, onSwitch }) {
  const rows = (board?.rows || []).slice(0, 18);
  return (
    <div className="fullscreen-board">
      <div className="fullscreen-topbar">
        <div className="brand-lockup">
          <span className="rail-icon">▰▰</span>
          <div>
            <strong>{board?.station || station} Train {boardLabel(page)}</strong>
            <span>{page === 'arrivals' ? 'Live arrivals board' : 'Live departure board'}</span>
          </div>
        </div>
        <div className="fullscreen-clock">{formatClock()}</div>
      </div>
      <div className="fullscreen-switcher">
        <button className={page === 'departures' ? 'active' : ''} onClick={() => onSwitch('departures')} type="button">Departures</button>
        <button className={page === 'arrivals' ? 'active' : ''} onClick={() => onSwitch('arrivals')} type="button">Arrivals</button>
      </div>
      <BoardTable rows={rows} page={page} compact />
      <div className="fullscreen-footer">
        <span>National Rail Darwin live information · {board?.source === 'demo' ? 'Demo mode' : 'Live data'}</span>
        <button onClick={onClose} type="button">Exit board</button>
      </div>
    </div>
  );
}

export default function App() {
  const route = useMemo(() => initialRoute(), []);
  const [stations, setStations] = useState(DEFAULT_STATIONS);
  const [station, setStation] = useState(route.station);
  const [page, setPage] = useState(route.page);
  const [full, setFull] = useState(route.full);
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [filter, setFilter] = useState('');

  const selectedStation = useMemo(() => stations.find((s) => s.crs === station) || { crs: station, name: station }, [stations, station]);

  async function loadBoard(crs = station, nextPage = page) {
    setLoading(true);
    setError('');
    try {
      const endpoint = nextPage === 'arrivals' ? 'arrivals' : 'departures';
      const res = await fetch(`${API_BASE}/api/trains/${endpoint}?station=${encodeURIComponent(crs)}&rows=32`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Train board unavailable');
      setBoard(data);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err.message || 'Train board unavailable');
    } finally {
      setLoading(false);
    }
  }

  function changePage(nextPage) {
    setPage(nextPage);
    updateUrl({ page: nextPage, station, full });
    loadBoard(station, nextPage);
  }

  function changeStation(nextStation) {
    setStation(nextStation);
    updateUrl({ page, station: nextStation, full });
  }

  function openFull() {
    setFull(true);
    updateUrl({ page, station, full: true });
  }

  function closeFull() {
    setFull(false);
    updateUrl({ page, station, full: false });
  }

  useEffect(() => {
    fetch(`${API_BASE}/api/stations`).then((r) => r.json()).then((data) => {
      if (Array.isArray(data.stations)) setStations(data.stations);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    loadBoard(station, page);
    const timer = setInterval(() => loadBoard(station, page), 60000);
    return () => clearInterval(timer);
  }, [station, page]);

  const filteredStations = stations.filter((s) => `${s.name} ${s.crs}`.toLowerCase().includes(filter.toLowerCase()));

  if (full) {
    return <FullscreenBoard board={board} page={page} station={selectedStation.name} onClose={closeFull} onSwitch={changePage} />;
  }

  return (
    <main className="app-shell">
      <header className="app-header">
        <div className="hero-copy">
          <div className="eyebrow">Plymouth Rail</div>
          <h1>Train information</h1>
          <p>Mobile-friendly live departures and arrivals for Plymouth and nearby stations.</p>
        </div>
        <div className="header-status">
          <strong>{loading ? 'Updating…' : lastUpdated ? `Updated ${formatClock(lastUpdated)}` : 'Ready'}</strong>
          <span>{board?.source === 'demo' ? 'Demo data' : 'Darwin live data'}</span>
        </div>
      </header>

      <nav className="top-tabs" aria-label="Board type">
        <button className={page === 'departures' ? 'active' : ''} onClick={() => changePage('departures')} type="button">Departures</button>
        <button className={page === 'arrivals' ? 'active' : ''} onClick={() => changePage('arrivals')} type="button">Arrivals</button>
        <button className="full-link" onClick={openFull} type="button">⛶ Fullboard</button>
      </nav>

      <section className="controls-card">
        <label>
          Station
          <select value={station} onChange={(e) => changeStation(e.target.value)}>
            {stations.map((s) => <option key={s.crs} value={s.crs}>{s.name} ({s.crs})</option>)}
          </select>
        </label>
        <button onClick={() => loadBoard(station, page)} disabled={loading} type="button">↻ Refresh</button>
      </section>

      <section className="mobile-station-search">
        <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search station or CRS…" />
        <div className="station-chips">
          {filteredStations.slice(0, 12).map((s) => (
            <button className={s.crs === station ? 'active' : ''} key={s.crs} onClick={() => changeStation(s.crs)} type="button">
              {s.name} <span>{s.crs}</span>
            </button>
          ))}
        </div>
      </section>

      <section className="main-grid">
        <aside className="station-list-card">
          <h2>Stations</h2>
          <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search station or CRS…" />
          <div className="station-list">
            {filteredStations.map((s) => (
              <button className={s.crs === station ? 'active' : ''} key={s.crs} onClick={() => changeStation(s.crs)} type="button">
                <strong>{s.name}</strong>
                <span>{s.crs}</span>
              </button>
            ))}
          </div>
        </aside>

        <section className="board-card">
          <div className="board-title-row">
            <div>
              <h2>{board?.station || selectedStation.name} {boardLabel(page)}</h2>
              <p>{selectedStation.crs} · {page === 'arrivals' ? 'Origin, platform and expected arrival time' : 'Destination, platform and expected departure time'}</p>
            </div>
            <div className="clock-card">{formatClock()}</div>
          </div>

          <div className="quick-links">
            <a href={`/departures?station=${station}`}>Departures link</a>
            <a href={`/arrivals?station=${station}`}>Arrivals link</a>
            <a href={`/fullboard?station=${station}&type=${page}`}>Fullboard link</a>
          </div>

          {error && <div className="error-box">{error}</div>}
          {board?.note && <div className="note-box">{board.note}</div>}
          {loading && !board && <div className="loading-box">Loading {boardLabel(page).toLowerCase()}…</div>}
          <BoardTable rows={board?.rows || []} page={page} />
        </section>
      </section>
    </main>
  );
}
